<template>
  <div class="availability-form-unavailability-item">
    <div
      class="surface space-y-2 rounded-md p-2"
      :class="{ 'pointer-events-none opacity-50': isDeleting }"
    >
      <div class="grid grid-cols-12 gap-x-2">
        <div class="col-span-6 grid grid-cols-12 gap-x-2">
          <label class="col-span-8">
            <span class="block text-sm font-medium select-none"> vanaf </span>
            <InputDatepicker
              v-model="startDate"
              placeholder="DD-MM-YY"
              :disabled="disabled"
              @update:model-value="delayedUpdate.schedule()"
              class="border-gray-lighten-1 rounded-md border border-solid"
            />
          </label>
          <label class="col-span-4">
            <span class="invisible block text-sm font-medium">Starttijd</span>
            <InputTimepicker
              v-model="startTime"
              :date="startDate"
              :disabled="disabled"
              @focusout="delayedUpdate.schedule()"
              class="border-gray-lighten-1 border-solid"
            />
          </label>
        </div>
        <div class="col-span-6 grid grid-cols-12 gap-x-2">
          <label class="col-span-8">
            <span class="block text-sm font-medium select-none">tot en met</span>
            <InputDatepicker
              v-model="endDate"
              placeholder="DD-MM-YY"
              :disabled="disabled || !startDate"
              @update:model-value="delayedUpdate.schedule()"
              class="border-gray-lighten-1 rounded-md border border-solid"
            />
          </label>
          <label class="col-span-4">
            <span class="invisible block text-sm font-medium">Eindtijd</span>
            <InputTimepicker
              v-model="endTime"
              :date="endDate"
              :disabled="disabled || !endDate"
              @focusout="delayedUpdate.schedule()"
              class="border-gray-lighten-1 border-solid"
            />
          </label>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-x-2">
        <div class="col-span-3 flex flex-col space-y-1">
          <label>
            <span
              class="after:text-secondary block text-sm font-medium select-none after:content-['*']"
            >
              Oorzaak
            </span>
            <InputSelect
              v-model="modelValue.reason"
              :items="availabilityReasons"
              items-key-name="id"
              items-value-name="label"
              placeholder="Keuze"
              :disabled="disabled"
              @select="delayedUpdate.schedule()"
              class="border-gray-lighten-1 rounded-md border border-solid"
            />
          </label>

          <div>
            <GeneralButton
              :icon="['fas', 'trash-can']"
              title="Afwezigheid verwijderen"
              :disabled="disabled"
              size="small"
              @click="onClickDelete"
              >Verwijderen</GeneralButton
            >
          </div>
        </div>

        <div class="col-span-9">
          <label>
            <span class="block text-sm font-medium select-none">Opmerking</span>
            <InputTextarea
              v-model="modelValue.description"
              placeholder="Verdere toelichting"
              :disabled="disabled"
              @input="delayedUpdate.schedule()"
              class="border-gray-lighten-1 border-solid"
              autosize
            />
          </label>
        </div>
      </div>
      <FormValidationAlert v-model="v$.range" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from "vue"
import useVuelidate from "@vuelidate/core"
import type { Moment } from "moment"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import InputDatepicker from "@/components/inputs/input_datepicker/InputDatepicker.vue"
import InputSelect from "@/components/inputs/InputSelect.vue"
import InputTextarea from "@/components/inputs/InputTextarea.vue"
import InputTimepicker from "@/components/inputs/InputTimepicker.vue"
import type { AvailabilityBlock } from "@/models/availability-block.model.ts"
import type { AvailabilityReason } from "@/models/availability-reason.model.ts"
import {
  getDatePortion,
  getTimePortion,
  setDatePortion,
  setTimePortion
} from "@/helper/time-helper.ts"
import type { AvailabilityBlockController } from "@/controllers/model-controllers/availability-block-controller.ts"
import useEntityUpdater from "@/composables/api/entity-updater.ts"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import { chronologicalStartAndEndTimeValidator } from "@/helper/validation/time-validation.ts"
import FormValidationAlert from "@/components/validation/FormValidationAlert.vue"

const modelValue = defineModel<AvailabilityBlock>({ required: true })

withDefaults(
  defineProps<{
    availabilityReasons: AvailabilityReason[]
    disabled?: boolean
  }>(),
  {
    disabled: false
  }
)

const emit = defineEmits<{
  hasError: [id: string, value: boolean]
  save: []
}>()

const availabilityBlockController = inject<AvailabilityBlockController>(
  "availabilityBlockController"
)!
const entityUpdater = useEntityUpdater(
  modelValue,
  availabilityBlockController.update,
  availabilityBlockController.create
)
const delayedUpdate = useDelayedFunction(() => {
  if (v$.value.range?.$invalid) {
    return
  }
  entityUpdater.update()
  emit("save")
}, 300)

const isDeleting = ref(false)
function onClickDelete() {
  isDeleting.value = true
  availabilityBlockController.delete(modelValue.value)
}

// Split timeStart into separate date and time computed refs for the input controls.
const startDate = computed<Moment | null>({
  get: () => getDatePortion(modelValue.value.timeStart),
  set: (value) => (modelValue.value.timeStart = setDatePortion(modelValue.value.timeStart, value))
})

const startTime = computed<Moment | null>({
  get: () => getTimePortion(modelValue.value.timeStart),
  set: (value) => (modelValue.value.timeStart = setTimePortion(modelValue.value.timeStart, value))
})

// Split timeEnd into separate date and time computed refs for the input controls.
const endDate = computed<Moment | null>({
  get: () => getDatePortion(modelValue.value.timeEnd),
  set: (value) => (modelValue.value.timeEnd = setDatePortion(modelValue.value.timeEnd, value))
})

const endTime = computed<Moment | null>({
  get: () => getTimePortion(modelValue.value.timeEnd),
  set: (value) => (modelValue.value.timeEnd = setTimePortion(modelValue.value.timeEnd, value))
})

// --- Validation ---

const validationRules = computed(() => ({
  range: {
    timeRange: chronologicalStartAndEndTimeValidator({
      allowEqual: true,
      treatMidnightAsEndOfDay: true
    })
  }
}))

const validationState = computed(() => ({
  range: modelValue.value
}))

const v$ = useVuelidate(validationRules, validationState)

watch(
  () => v$.value.range?.$invalid,
  (value) => {
    if (modelValue.value.id) {
      emit("hasError", modelValue.value.id, value)
    }
  }
)
</script>

<style scoped></style>
